import React, { useState } from "react";
import { useDropzone } from "react-dropzone";
import ClearIcon from "@mui/icons-material/Clear";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { IconButton, Tooltip } from "@mui/material";
import axios from "axios";
import { API_URL } from "const/constants";
import useKothar from "context/useKothar";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import InputField from "./InputField";
import { Delete } from "@mui/icons-material";
import { FaCloudUploadAlt } from "react-icons/fa";
import { getFileIcon } from "utils/utils";
import { getOriginalFileName, stripUrlParams } from "utils/utils";
import { MAX_UPLOAD_FILE_SIZE_BYTES, MAX_UPLOAD_FILE_SIZE_LABEL } from "constants/fileUploadLimits";

function isImageFile(filePath) {
  if (!filePath) return false;
  const extension = stripUrlParams(filePath).split(".").pop()?.toLowerCase();
  return ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(extension);
}

const DEFAULT_MAX_SIZE = MAX_UPLOAD_FILE_SIZE_BYTES;

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const UploadFile = ({
  data,
  setData,
  imageKey,
  label = "Document",
  type = "single",
  required = false,
  newDesign = true,
  className,
  disabled = false,
  maxSize = DEFAULT_MAX_SIZE,
}) => {
  const [{ token }, { setWholeLoading }] = useKothar();
  const [imageErrors, setImageErrors] = useState({});

  const uploadFile = (file) => {
    if (file.size > maxSize) {
      toast.error(
        `File is too large (${formatFileSize(file.size)}). Maximum allowed size is ${formatFileSize(
          maxSize
        )}.`
      );
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    axios
      .post(`${API_URL}/file/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        toast.success("File Uploaded Successfully");
        if (type === "multiple") {
          setData({
            ...data,
            [imageKey]: [...(data?.[imageKey] || []), res?.data?.url],
          });
        } else {
          setData({ ...data, [imageKey]: res?.data?.url });
        }
      })
      .catch((err) => {
        const rawStatus =
          err?.response?.status ?? err?.request?.status ?? err?.response?.data?.statusCode;
        const status = Number(rawStatus);

        if (status === 413) {
          toast.error("File is too large, please reduce the file size.");
        } else {
          toast.error(err?.response?.data?.errorMessage || "Error uploading file");
        }
      });
  };

  const onDrop = (acceptedFiles) => {
    if (type === "multiple") {
      acceptedFiles.forEach(uploadFile);
    } else if (acceptedFiles[0]) {
      uploadFile(acceptedFiles[0]);
    }
  };

  const onDropRejected = (rejections) => {
    const fileTooLarge = rejections.some((r) => r.errors?.some((e) => e.code === "file-too-large"));
    if (fileTooLarge) {
      toast.error(`File is too large. Maximum allowed size is ${formatFileSize(maxSize)}.`);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    multiple: type === "multiple",
    maxFiles: type === "multiple" ? undefined : 1,
    maxSize,
  });

  const handleDeletePdf = (name, i) => {
    setData({
      ...data,
      [imageKey]: [...data?.[imageKey]?.filter((item, index) => i !== index)],
    });
  };

  return (
    <div className={`w-full mb-3 ${className}`}>
      {type !== "multiple" ? (
        <div className="space-y-4">
          {newDesign ? (
            <>
              <label className="block text-sm font-medium text-app-text mb-2">
                {`Upload ${label}` || "Upload Image"}
                {required && <span className="text-red-500 ml-1">*</span>}
              </label>{" "}
              <div className="relative">
                <div
                  {...getRootProps()}
                  className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors duration-200 ${
                    isDragActive
                      ? "border-blue-400 bg-blue-50"
                      : "border-app-border bg-app-surface-muted hover:bg-app-surface-hover"
                  }`}
                >
                  <input {...getInputProps()} />
                  <FaCloudUploadAlt className="w-10 h-10 mb-3 text-app-text-muted" />
                  <p className="mb-2 text-sm text-app-text-secondary">
                    <span className="font-semibold">
                      Click or drag file to upload. File must be less than{" "}
                      {MAX_UPLOAD_FILE_SIZE_LABEL}
                    </span>
                  </p>
                  <p className="text-xs text-app-text-secondary">{label}</p>
                </div>
              </div>
            </>
          ) : (
            <InputField
              label={`Upload ${label}` || "Upload Image"}
              name="image"
              required={required}
              type="file"
              onChange={(e) => uploadFile(e.target.files[0])}
              disabled={disabled}
            />
          )}
          {data?.[imageKey] && (
            <div className="relative bg-app-surface rounded-lg shadow-sm border border-app-border overflow-hidden">
              <div className="p-4 flex items-center space-x-3">
                {isImageFile(data?.[imageKey]) && !imageErrors[data?.[imageKey]] ? (
                  <div className="w-16 h-16 bg-app-surface-muted rounded flex items-center justify-center flex-shrink-0 overflow-hidden border border-app-border">
                    <img
                      src={data?.[imageKey]}
                      alt={getOriginalFileName(data?.[imageKey]) || "Preview"}
                      className="w-full h-full object-cover"
                      onError={() => {
                        setImageErrors((prev) => ({
                          ...prev,
                          [data?.[imageKey]]: true,
                        }));
                      }}
                    />
                  </div>
                ) : (
                  <div className="w-8 h-8 bg-app-surface-muted rounded flex items-center justify-center flex-shrink-0">
                    {getFileIcon(data?.[imageKey])}
                  </div>
                )}
                <span className="text-sm text-app-text truncate flex-1">
                  {getOriginalFileName(data?.[imageKey]) || "File"}
                </span>
                <div className="flex items-center space-x-2 ml-auto">
                  <Tooltip title="Download">
                    <Link
                      to={data?.[imageKey]}
                      download
                      target="_blank"
                      className="text-app-text-secondary hover:text-primary"
                    >
                      <FileDownloadIcon className="text-sm" />
                    </Link>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <button
                      onClick={() => {
                        if (window.confirm("Are you sure you want to delete this file?")) {
                          setData({ ...data, [imageKey]: null });
                        }
                      }}
                      className="text-app-text-secondary hover:text-red-600"
                    >
                      <ClearIcon className="text-sm" />
                    </button>
                  </Tooltip>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {newDesign ? (
            <>
              {" "}
              <label className="block text-sm font-medium text-app-text mb-2">
                {`Upload ${label}` || "Upload Image"}
                {required && <span className="text-red-500 ml-1">*</span>}
              </label>
              <div className="relative">
                <div
                  {...getRootProps()}
                  className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors duration-200 ${
                    isDragActive
                      ? "border-blue-400 bg-blue-50"
                      : "border-app-border bg-app-surface-muted hover:bg-app-surface-hover"
                  }`}
                >
                  <input {...getInputProps()} />
                  <FaCloudUploadAlt className="w-10 h-10 mb-3 text-app-text-muted" />
                  <p className="mb-2 text-sm text-app-text-secondary">
                    <span className="font-semibold">
                      Click or drag file to upload. File must be less than{" "}
                      {MAX_UPLOAD_FILE_SIZE_LABEL}
                    </span>
                  </p>
                  <p className="text-xs text-app-text-secondary">Multiple {label}s allowed</p>
                </div>
              </div>
            </>
          ) : (
            <InputField
              label={`Upload ${label}` || "Upload Image"}
              name="image"
              required={required}
              type="file"
              onChange={(e) => uploadFile(e.target.files[0])}
              disabled={disabled}
            />
          )}

          {data?.[imageKey]?.length > 0 && (
            <div className="space-y-2">
              {data?.[imageKey]?.map((item, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between bg-app-surface rounded-lg shadow-sm border border-app-border p-3 hover:shadow-md transition-shadow duration-200"
                >
                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                    {isImageFile(item) && !imageErrors[item] ? (
                      <div className="w-16 h-16 bg-app-surface-muted rounded flex items-center justify-center flex-shrink-0 overflow-hidden border border-app-border">
                        <img
                          src={item}
                          alt={getOriginalFileName(item) || "Preview"}
                          className="w-full h-full object-cover"
                          onError={() => {
                            setImageErrors((prev) => ({
                              ...prev,
                              [item]: true,
                            }));
                          }}
                        />
                      </div>
                    ) : (
                      <div className="w-8 h-8 bg-app-surface-muted rounded flex items-center justify-center flex-shrink-0">
                        {getFileIcon(item)}
                      </div>
                    )}
                    <span className="text-sm text-app-text truncate">
                      {getOriginalFileName(item) || "File"}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0 ml-4">
                    <Tooltip title="Download">
                      <Link
                        to={item}
                        download
                        target="_blank"
                        className="text-app-text-secondary hover:text-primary"
                      >
                        <FileDownloadIcon className="text-sm" />
                      </Link>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <button
                        onClick={() => {
                          if (window.confirm("Are you sure you want to delete this file?")) {
                            handleDeletePdf(imageKey, i);
                          }
                        }}
                        className="text-app-text-secondary hover:text-red-600"
                      >
                        <Delete className="text-sm" />
                      </button>
                    </Tooltip>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UploadFile;
